import axios, { type AxiosInstance, type InternalAxiosRequestConfig } from 'axios';
import { reportNetworkError } from '@/composables/useBackendStatus';
import { recordApiTiming } from '@/composables/useApiTimings';

export const apiClient: AxiosInstance = axios.create({
  baseURL: '',
  withCredentials: true,
  timeout: 30000,
  headers: {
    'Content-Type': 'application/json',
  },
});

// ── Interceptor auto-refresh ───────────────────────────────────────────────
//
// Quando l'access token scade (401), tenta di rinnovarlo automaticamente
// via POST /auth/refresh (il server legge il refresh_token dal cookie HttpOnly).
// Se il refresh riesce, riprova la request originale trasparentemente.
// Se fallisce (refresh token scaduto/revocato), redirige al login.
//
// Queue mechanism: se arrivano più 401 contemporaneamente durante il refresh,
// vengono accodate e risolte tutte insieme quando il refresh completa.

// ── Interceptor request: Accept-Language ───────────────────────────────────
//
// Propaga la lingua scelta dall'utente al backend (messaggi di errore/validazione
// localizzati + guida contestuale). Legge direttamente da localStorage per evitare
// un import circolare con il modulo i18n (che importa questo apiClient).
apiClient.interceptors.request.use((config) => {
  const lang = localStorage.getItem('i18n:locale');
  if (lang) {
    config.headers.set('Accept-Language', lang);
  }
  return config;
});

// ── Interceptor timing: durata delle chiamate (badge diagnostico widget) ────
//
// Registra la durata di OGNI chiamata in un registro in-memory (useApiTimings),
// letto dal badge passpartout dei widget dashboard. La partenza è agganciata
// alla config via WeakMap (l'oggetto config arriva identico nella response).
const requestStart = new WeakMap<object, number>();

function registraTiming(config: InternalAxiosRequestConfig | undefined, status: number): void {
  if (!config?.url) return;
  const t0 = requestStart.get(config);
  if (t0 === undefined) return;
  recordApiTiming({
    path: config.url.split('?')[0] ?? config.url,
    method: (config.method ?? 'get').toUpperCase(),
    ms: Math.round(performance.now() - t0),
    status,
    at: Date.now(),
  });
}

apiClient.interceptors.request.use((config) => {
  requestStart.set(config, performance.now());
  return config;
});

apiClient.interceptors.response.use(
  (response) => {
    registraTiming(response.config, response.status);
    return response;
  },
  (error) => {
    if (axios.isAxiosError(error)) {
      registraTiming(error.config as InternalAxiosRequestConfig, error.response?.status ?? 0);
    }
    return Promise.reject(error);
  },
);

let isRefreshing = false;
let refreshQueue: Array<() => void> = [];

function drainQueue() {
  refreshQueue.forEach(cb => cb());
  refreshQueue = [];
}

apiClient.interceptors.response.use(
  (response) => response,
  async (error) => {
    // Nessuna risposta = backend irraggiungibile (riavvio, rete, ecc.)
    // (una richiesta annullata di proposito non è un guasto)
    if (!error.response && !axios.isCancel(error)) {
      reportNetworkError({
        url:     error.config?.url,
        message: error.message,
      })
    }

    const status = error.response?.status;
    const originalRequest = error.config as InternalAxiosRequestConfig & { _retried?: boolean };
    const currentPath = window.location.pathname;

    // Gestione 401: prova il refresh prima di fare logout
    // Pagine PUBBLICHE (utente non autenticato per definizione): il 401 di /auth/me o simili
    // NON deve innescare refresh+redirect al login, altrimenti il link email di reset password
    // rimbalza su /login?redirect=... prima ancora di mostrare la pagina.
    // /landingpage: sito vetrina pubblico, stesso motivo — vedi router/index.ts.
    // /status: pagina di stato pubblica (deve funzionare proprio quando l'utente NON è loggato /
    // il backend è instabile) → un 401 di /auth/me non deve rimbalzarla su /login.
    const isPublicPage = currentPath === '/login' || currentPath.startsWith('/reset-password') ||
      currentPath === '/landingpage' || currentPath === '/status';
    if (
      status === 401 &&
      !originalRequest._retried &&
      !isPublicPage &&
      // Evita loop infinito se la 401 arriva dall'endpoint di refresh stesso
      !originalRequest.url?.includes('/auth/refresh') &&
      !originalRequest.url?.includes('/auth/login')
    ) {
      if (isRefreshing) {
        // Un refresh è già in corso: accoda questa request e aspetta
        return new Promise<void>((resolve) => {
          refreshQueue.push(resolve);
        }).then(() => {
          originalRequest._retried = true;
          return apiClient(originalRequest);
        });
      }

      originalRequest._retried = true;
      isRefreshing = true;

      try {
        // POST /auth/refresh senza body: il server legge il refresh_token dal cookie HttpOnly
        await axios.post('/auth/refresh', {}, { withCredentials: true });
        // Refresh riuscito: risolvi tutte le request in coda e riprova quella originale
        drainQueue();
        return apiClient(originalRequest);
      } catch (refreshError) {
        // Refresh fallito (token scaduto/revocato, o negato per policy di sessione): logout.
        // Se il backend risponde con { error: 'SESSION_EXPIRED', reason: 'idle'|'absolute'|'revoked' }
        // ('revoked' = sessione chiusa da un amministratore dal Log Utenti)
        // (SessionPolicyService), propaga il motivo al login così l'utente vede un messaggio
        // chiaro invece del generico "inattività" da timer client.
        refreshQueue = [];
        if (!isPublicPage) {
          const refreshErrorData = axios.isAxiosError(refreshError)
            ? (refreshError.response?.data as { error?: string; reason?: string } | undefined)
            : undefined;
          let redirectUrl = `/login?redirect=${encodeURIComponent(currentPath)}`;
          if (refreshErrorData?.error === 'SESSION_EXPIRED' &&
              (refreshErrorData.reason === 'idle' || refreshErrorData.reason === 'absolute'
                || refreshErrorData.reason === 'revoked')) {
            redirectUrl += `&reason=${refreshErrorData.reason}`;
          }
          window.location.href = redirectUrl;
        }
        return Promise.reject(error);
      } finally {
        isRefreshing = false;
      }
    }

    return Promise.reject(error);
  }
);
